import React from "react";
import PropTypes from "prop-types";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

import getStyled from "../../../hooks/getStyled";

const { theme } = getStyled();

const CustomLabels = (props) => {
  const RADIAN = Math.PI / 180;
  const { cx, cy, midAngle, innerRadius, outerRadius, payload, percent } =
    props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 5) * cos;
  const sy = cy + (outerRadius + 5) * sin;
  const mx = cx + (outerRadius + 5 + 30 * Math.abs(sin)) * cos;
  const my = cy + (outerRadius + 5 + 30 * Math.abs(cos)) * sin;
  const ex =
    mx + (payload.isTarget ? 1 : -1) * (50 + 60 * sin ** 8 + 20 * cos ** 2);
  const ey = my;
  const textAnchor = payload.isTarget ? "end" : "start";

  const percentage = (percent * 100).toFixed();

  // Spacing between value and % sign
  const s = 1;
  // Length of value
  const l1 = percentage.length * 11;
  // Length of % sign
  const l2 = 8;
  // Value displacement
  const dx1 = (s + l2) / 2;
  // % sign displacement
  const dx2 = (s + l1) / 2;
  return (
    <g>
      {payload.isTarget && (
        <>
          <circle
            cx={cx}
            cy={cy}
            r={innerRadius / 1.35}
            fill={theme.palette.grey200.main}
          />
          <text
            fontSize={18}
            fontWeight={700}
            x={cx - dx1}
            y={cy}
            dy={8}
            textAnchor="middle"
            fill={theme.palette.black.main}
          >
            {percentage}
          </text>
          <text
            fontSize={12}
            fontWeight={300}
            x={cx + dx2}
            y={cy}
            dy={8}
            textAnchor="middle"
            fill={theme.palette.black.main}
          >
            %
          </text>
        </>
      )}

      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        fill="none"
        stroke={theme.palette.grey200.main}
      />
      <text
        x={ex}
        y={ey - 5}
        textAnchor={textAnchor}
        fontSize={16}
        fontWeight={700}
        fill={theme.palette.grey500.main}
      >
        {payload.value}
      </text>
      <text
        x={ex}
        y={ey}
        dy={18}
        textAnchor={textAnchor}
        fill={theme.palette.grey400.main}
        fontSize={12}
        fontWeight={300}
      >
        {payload.name}
      </text>
    </g>
  );
};

const PIE_LAYER_DATA = [{ value: 100 }];

const getChartColor = (percent) => {
  let color = "";
  if (percent <= 25) {
    color = theme.palette.red300.main;
  } else if (percent > 25 && percent <= 50) {
    color = theme.palette.orange300.main;
  } else {
    color = theme.palette.green300.main;
  }
  return color;
};

const SFTargetPieChart = ({
  data,
  width = 250,
  height = 250,
  dataKey = "value",
  targetKeyName,
}) => {
  const targetValue = data.find((x) => x.name === targetKeyName).value;
  const totalValue = data.reduce((acc, cur) => acc.value + cur.value);
  const percent = (targetValue / totalValue) * 100;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart width={width} height={height}>
        <Pie
          isAnimationActive={false}
          paddingAngle={0}
          data={PIE_LAYER_DATA}
          innerRadius={width / 4}
          outerRadius={width / 3.5}
          fill={theme.palette.grey200.main}
          dataKey="value"
          blendStroke
        />
        <Pie
          isAnimationActive={false}
          labelLine={false}
          label={CustomLabels}
          paddingAngle={0}
          startAngle={90}
          endAngle={-270}
          data={data.map((x) => ({ ...x, isTarget: x.name === targetKeyName }))}
          innerRadius={width / 4}
          outerRadius={width / 3.5}
          fill="transparent"
          dataKey={dataKey}
          cornerRadius={50}
          blendStroke
        >
          <Cell fill={getChartColor(percent)} />
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
};

SFTargetPieChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  targetKeyName: PropTypes.string.isRequired,
  dataKey: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
};

export default SFTargetPieChart;
